import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TaskyAlert, TaskyBadge, TaskyButton, TaskyProgress } from '../tasky';
import adminVideoStyleRecipeService, {
  isTrialRunning,
  type AdminVideoStyleRecipe,
  type VideoStyleTrial,
  type VideoStyleTrialDetail,
} from '../../services/api/adminVideoStyleRecipeService';
import VideoStyleTrialPlayer from './VideoStyleTrialPlayer';

const POLL_MS = 5000;

const STATUS_LABEL: Record<VideoStyleTrial['status'], { label: string; variant: 'info' | 'success' | 'destructive' }> = {
  pending: { label: '待機中', variant: 'info' },
  running: { label: '作成中', variant: 'info' },
  completed: { label: '完成', variant: 'success' },
  failed: { label: '失敗', variant: 'destructive' },
};

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

interface Props {
  recipeId: number;
  /** 試作を始めたら増やす（すぐ一覧を取り直す） */
  refreshKey: number;
  /** 完成した試作から見本を作り始めた */
  onSampleStarted?: (recipe: AdminVideoStyleRecipe) => void;
}

/**
 * スタイル詳細の「試作」: 一覧（状態・進捗・商品・作成日）と、完成した試作の動画・ダウンロード。
 * 作成中の試作がある間だけ一覧を取り直す。見えないタブでは止め、前の取得が終わるまで次を出さない。
 */
const VideoStyleTrialSection: React.FC<Props> = ({ recipeId, refreshKey, onSampleStarted }) => {
  const [trials, setTrials] = useState<VideoStyleTrial[]>([]);
  const [selected, setSelected] = useState<VideoStyleTrialDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const list = await adminVideoStyleRecipeService.trials(recipeId);
      setTrials(list.trials);
      setError(null);
    } catch {
      setError('試作の一覧を読み込めませんでした。');
    } finally {
      inFlight.current = false;
    }
  }, [recipeId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const running = trials.some(isTrialRunning);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [running, load]);

  const open = useCallback(async (trial: VideoStyleTrial) => {
    try {
      setSelected(await adminVideoStyleRecipeService.trial(trial.id));
    } catch {
      setError('試作の動画を読み込めませんでした。');
    }
  }, []);

  // 新しく完成した試作は自動で開く（まだ何も開いていないときだけ）
  const latestCompleted = trials.find((t) => t.status === 'completed' && t.has_video);
  useEffect(() => {
    if (selected === null && latestCompleted) void open(latestCompleted);
  }, [latestCompleted, selected, open]);

  return (
    <div className="flex flex-col gap-2" data-testid="video-style-trials">
      <p className="text-sm font-medium">試作</p>
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}
      {trials.length === 0 ? (
        <p className="text-sm text-gray-500">まだ試作はありません。</p>
      ) : (
        <ul className="flex flex-col divide-y divide-warm-border rounded-tasky-sm border border-warm-border">
          {trials.map((trial) => {
            const status = STATUS_LABEL[trial.status];
            return (
              <li key={trial.id} className="flex flex-wrap items-center gap-3 p-2 text-sm" data-testid="video-style-trial-row">
                <TaskyBadge variant={status.variant} dot={isTrialRunning(trial)}>
                  {status.label}
                </TaskyBadge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-dark">{trial.product_name ?? `商品 #${trial.product_id}`}</span>
                  <span className="block text-xs text-gray-500">
                    {trial.organization_name ?? '—'}・{formatDate(trial.created_at)}・{trial.trial_credits_used.toLocaleString()}
                    {trial.trial_credits_estimated !== null ? ` / 目安 ${trial.trial_credits_estimated.toLocaleString()}` : ''} クレジット相当
                  </span>
                </span>
                {isTrialRunning(trial) && <TaskyProgress value={trial.progress} className="w-32" label={`${trial.progress}%`} />}
                {((trial.status === 'completed' && trial.has_video) || trial.status === 'failed') && (
                  <TaskyButton size="sm" variant={selected?.id === trial.id ? 'secondary' : 'ghost'} onClick={() => void open(trial)}>
                    {trial.status === 'failed' ? '理由' : '見る'}
                  </TaskyButton>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {selected && <VideoStyleTrialPlayer trial={selected} recipeId={recipeId} onSampleStarted={onSampleStarted} />}
    </div>
  );
};

export default VideoStyleTrialSection;
